/**
 * React wrapper for Bryntum CalendarField
 */
import React, { RefObject } from 'react';
import { AlignSpec, Base, CalendarField, CalendarManagerStore, ChipViewConfig, CollectionFilterConfig, Combo, ContainerItemConfig, Field, FieldContainer, FieldContainerConfig, FieldTriggerConfig, List, ListConfig, MaskConfig, Model, Rectangle, Scroller, ScrollerConfig, Store, StoreConfig, TabConfig, TooltipConfig, Widget } from '@bryntum/schedulerpro';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumCalendarFieldProps =  {
     // Configs only
    adopt?                   : HTMLElement|string
    align?                   : AlignSpec|string
    anchor?                  : boolean
    ariaDescription?         : string
    ariaLabel?               : string
    autoClose?               : boolean
    autoComplete?            : string
    autoExpand?              : boolean
    autoSelect?              : boolean
    bubbleEvents?            : object
    callOnFunctions?         : boolean
    caseSensitive?           : boolean
    centered?                : boolean
    chipView?                : Partial<ChipViewConfig>
    clearable?               : boolean|Partial<FieldTriggerConfig>
    clearTextOnPickerHide?   : boolean
    config?                  : object
    constrainTo?             : HTMLElement|Widget|Rectangle
    container?               : Record<string, Partial<ContainerItemConfig>>|Partial<ContainerItemConfig>[]|Partial<FieldContainerConfig>|FieldContainer
    containValues?           : boolean|string|((field: Field) => boolean)
    contentElementCls?       : string|object
    createOnUnmatched?       : ((name: string, combo: Combo) => Model)|string|boolean
    defaultBindProperty?     : string
    displayField?            : string
    displayValueRenderer?    : (record: Model, combo: Combo) => string|void
    dock?                    : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?               : boolean|{
        handleSelector?: string
    }
    emptyText?               : string
    encodeFilterParams?      : (filters: object[]) => object[]
    filterOnEnter?           : boolean
    filterParamName?         : string
    filterSelected?          : boolean
    floating?                : boolean
    hideAnimation?           : boolean|object
    hidePickerOnSelect?      : boolean
    hideTrigger?             : boolean
    highlightExternalChange? : boolean
    hint?                    : string|((data: { source: Field, value: any }) => string)
    hintHtml?                : string|((data: { source: Field, value: any }) => string)
    htmlCls?                 : string|object
    ignoreParentReadOnly?    : boolean
    inline?                  : boolean
    inlinePicker?            : boolean
    inputAlign?              : string
    inputAttributes?         : object
    inputType?               : string
    inputWidth?              : string|number
    items?                   : object[]|string[]|object
    keyStrokeChangeDelay?    : number
    keyStrokeFilterDelay?    : number
    labelCls?                : string|object
    labelPosition?           : 'before'|'above'
    labels?                  : object[]
    labelWidth?              : string|number
    listCls?                 : string
    listeners?               : object
    listItemTpl?             : (record: Model) => string|void
    localeClass?             : typeof Base
    localizable?             : boolean
    localizableProperties?   : string[]
    maskDefaults?            : Partial<MaskConfig>
    masked?                  : boolean|string|Partial<MaskConfig>
    maxLength?               : number
    minChars?                : number
    minLength?               : number
    monitorResize?           : boolean
    multiSelect?             : boolean
    multiValueSeparator?     : string
    name?                    : string
    overlayAnchor?           : boolean
    owner?                   : Widget
    pickerAlignElement?      : string
    pickerWidth?             : number
    placeholder?             : string
    positioned?              : boolean
    preventTooltipOnTouch?   : boolean
    primaryFilter?           : Partial<CollectionFilterConfig>
    relayStoreEvents?        : boolean
    required?                : boolean
    revertOnEscape?          : boolean
    ripple?                  : boolean|{
        delegate?: string
        color?: string
        radius?: number
        clip?: string
    }
    rootElement?             : ShadowRoot
    scrollAction?            : 'hide'|'realign'|null
    showAnimation?           : boolean|object
    showTooltipWhenDisabled? : boolean
    spellCheck?              : boolean
    tab?                     : boolean|Partial<TabConfig>
    tabIndex?                : number
    tag?                     : string
    textAlign?               : 'left'|'center'|'right'|'start'|'end'
    title?                   : string
    triggerAction?           : 'all'|'last'|null
    ui?                      : string|object
    validateFilter?          : boolean
    validateOnInput?         : boolean
    valueField?              : string|null
    weight?                  : number

     // Configs and properties
    alignSelf?        : string
    appendTo?         : HTMLElement|string
    badge?            : string
    cls?              : string|object
    content?          : string
    dataset?          : object
    disabled?         : boolean
    editable?         : boolean
    extraData?        : string|number|boolean|object
    filterOperator?   : '='|'!='|'>'|'>='|'<'|'<='|'*'|'startsWith'|'endsWith'|'isIncludedIn'
    flex?             : number|string
    height?           : number|string
    hidden?           : boolean
    html?             : string|((widget: Widget) => string)
    id?               : string
    insertBefore?     : HTMLElement|string
    insertFirst?      : HTMLElement|string
    label?            : string
    margin?           : number|string
    maxHeight?        : string|number
    maximizeOnMobile? : number|string
    maxWidth?         : string|number
    minHeight?        : string|number
    minWidth?         : string|number
    picker?           : Partial<ListConfig>|List
    readOnly?         : boolean
    scrollable?       : Scroller|boolean|Partial<ScrollerConfig>
    store?            : Store|Partial<StoreConfig>|CalendarManagerStore
    tooltip?          : string|Partial<TooltipConfig>
    triggers?         : Record<string, Widget>|Record<string, FieldTriggerConfig>
    value?            : object|number|string|string[]|number[]
    width?            : number|string
    x?                : number
    y?                : number

     // Properties only
    anchorSize? : number[]
    input?      : HTMLElement
    type?       : string

     // Events
    onAction?        : ((event: { source: Combo, value: any, record: Model, records: Model[], userAction: boolean }) => void)|string
    onBeforeDestroy? : ((event: { source: Base }) => void)|string
    onBeforeHide?    : ((event: { source: Widget }) => boolean|void)|string
    onBeforeShow?    : ((event: { source: Widget|any }) => Promise<boolean|void>)|string
    onCatchAll?      : ((event: {[key: string]: any, type: string}) => void)|string
    onChange?        : ((event: { source: Field|any, value: string|number|boolean|any, oldValue: string|number|boolean|any, valid: boolean, event: Event, userAction: boolean, checked: boolean }) => void)|string
    onClear?         : ((event: { source: Field|any }) => void)|string
    onDestroy?       : ((event: { source: Base }) => void)|string
    onFocusIn?       : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onFocusOut?      : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onHide?          : ((event: { source: Widget }) => void)|string
    onInput?         : ((event: { source: Combo, value: string, event: Event }) => void)|string
    onPaint?         : ((event: { source: Widget, firstPaint: boolean }) => void)|string
    onReadOnly?      : ((event: { readOnly: boolean }) => void)|string
    onRecompose?     : (() => void)|string
    onResize?        : ((event: { source: Widget, width: number, height: number, oldWidth: number, oldHeight: number }) => void)|string
    onSelect?        : ((event: { source: Combo, record: Model, records: Model[], userAction: boolean }) => void)|string
    onShow?          : ((event: { source: Widget }) => void)|string
    onTrigger?       : ((event: { source: Field|any, trigger: Widget }) => void)|string

}

export class BryntumCalendarField extends React.Component<BryntumCalendarFieldProps> {

    static instanceClass = CalendarField;

    static instanceName = 'CalendarField';

    processWidgetContent = processWidgetContent;

    static configNames = [
        'adopt',
        'align',
        'anchor',
        'ariaDescription',
        'ariaLabel',
        'autoClose',
        'autoComplete',
        'autoExpand',
        'autoSelect',
        'bubbleEvents',
        'callOnFunctions',
        'caseSensitive',
        'centered',
        'chipView',
        'clearable',
        'clearTextOnPickerHide',
        'config',
        'constrainTo',
        'container',
        'containValues',
        'contentElementCls',
        'createOnUnmatched',
        'defaultBindProperty',
        'displayField',
        'displayValueRenderer',
        'dock',
        'draggable',
        'emptyText',
        'encodeFilterParams',
        'filterOnEnter',
        'filterParamName',
        'filterSelected',
        'floating',
        'hideAnimation',
        'hidePickerOnSelect',
        'hideTrigger',
        'highlightExternalChange',
        'hint',
        'hintHtml',
        'htmlCls',
        'ignoreParentReadOnly',
        'inline',
        'inlinePicker',
        'inputAlign',
        'inputAttributes',
        'inputType',
        'inputWidth',
        'items',
        'keyStrokeChangeDelay',
        'keyStrokeFilterDelay',
        'labelCls',
        'labelPosition',
        'labels',
        'labelWidth',
        'listCls',
        'listeners',
        'listItemTpl',
        'localeClass',
        'localizable',
        'localizableProperties',
        'maskDefaults',
        'masked',
        'maxLength',
        'minChars',
        'minLength',
        'monitorResize',
        'multiSelect',
        'multiValueSeparator',
        'name',
        'overlayAnchor',
        'owner',
        'pickerAlignElement',
        'pickerWidth',
        'placeholder',
        'positioned',
        'preventTooltipOnTouch',
        'primaryFilter',
        'relayStoreEvents',
        'required',
        'revertOnEscape',
        'ripple',
        'rootElement',
        'scrollAction',
        'showAnimation',
        'showTooltipWhenDisabled',
        'spellCheck',
        'tab',
        'tabIndex',
        'tag',
        'textAlign',
        'title',
        'triggerAction',
        'ui',
        'validateFilter',
        'validateOnInput',
        'valueField',
        'weight'
    ];

    static propertyConfigNames = [
        'alignSelf',
        'appendTo',
        'badge',
        'cls',
        'content',
        'dataset',
        'disabled',
        'editable',
        'extraData',
        'filterOperator',
        'flex',
        'height',
        'hidden',
        'html',
        'id',
        'insertBefore',
        'insertFirst',
        'label',
        'margin',
        'maxHeight',
        'maximizeOnMobile',
        'maxWidth',
        'minHeight',
        'minWidth',
        'onAction',
        'onBeforeDestroy',
        'onBeforeHide',
        'onBeforeShow',
        'onCatchAll',
        'onChange',
        'onClear',
        'onDestroy',
        'onFocusIn',
        'onFocusOut',
        'onHide',
        'onInput',
        'onPaint',
        'onReadOnly',
        'onRecompose',
        'onResize',
        'onSelect',
        'onShow',
        'onTrigger',
        'picker',
        'readOnly',
        'scrollable',
        'store',
        'tooltip',
        'triggers',
        'value',
        'width',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'input',
        'type'
    ];

    // Component instance
    instance!: CalendarField;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumCalendarFieldProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-calendar-field-container`;
        return (
            <div className={className} ref={(element) => (this.element = element!)}></div>
        );

    }
}
